import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/primitives/card";
import { Button } from "@repo/ui/primitives/button";
import { Input } from "@repo/ui/primitives/input";
import { Label } from "@repo/ui/primitives/label";
import { Textarea } from "@repo/ui/primitives/textarea";
import { useTranslation } from "react-i18next";

export function BreachPanel({ onMobileSwitch }) {
  const { t } = useTranslation("login");
  const [activeView, setActiveView] = useState("ca"); // "ca" | "docs"
  const [searchQuery, setSearchQuery] = useState("");

  const documents = useMemo(
    () => [
      {
        id: 1,
        title: t("breachPanel.documents.template.title", { defaultValue: "Data Breach Notification Template" }),
        description: t("breachPanel.documents.template.description", { defaultValue: "Standard notification template for data incidents" }),
      },
      {
        id: 2,
        title: t("breachPanel.documents.policy.title", { defaultValue: "Incident Response Policy" }),
        description: t("breachPanel.documents.policy.description", { defaultValue: "High-level public incident response overview" }),
      },
      {
        id: 3,
        title: t("breachPanel.documents.faq.title", { defaultValue: "Security & Privacy FAQ" }),
        description: t("breachPanel.documents.faq.description", { defaultValue: "Common questions for affected data subjects" }),
      },
      {
        id: 4,
        title: t("breachPanel.documents.rightsGuide.title", { defaultValue: "Data Subject Rights Guide" }),
        description: t("breachPanel.documents.rightsGuide.description", { defaultValue: "Summary of data subject rights and escalation paths" }),
      },
      {
        id: 5,
        title: t("breachPanel.documents.checklist.title", { defaultValue: "Breach Communication Checklist" }),
        description: t("breachPanel.documents.checklist.description", { defaultValue: "Checklist for outbound breach notifications" }),
      },
    ],
    [t]
  );

  const filteredDocuments = useMemo(
    () =>
      documents.filter((doc) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          doc.title.toLowerCase().includes(q) ||
          (doc.description && doc.description.toLowerCase().includes(q))
        );
      }),
    [documents, searchQuery]
  );
  return (
    <div className="flex h-full w-full items-center justify-center p-[var(--ds-space-4)] md:p-[var(--ds-space-10)]">
      <div className="w-full max-w-2xl space-y-[var(--ds-space-section)]">
        {activeView === "ca" ? (
          <Card className="border-border bg-card shadow-[var(--shadow-surface)]">
            <CardHeader className="space-y-2">
              <div className="space-y-1">
                <h2 className="text-2xl font-semibold tracking-tight">
                  {t("breachPanel.caCard.title", { defaultValue: "Breach Notification" })}
                </h2>
                <p className="text-sm text-muted-foreground break-all">
                  {t("breachPanel.caCard.description", { defaultValue: "Capture action details for security incidents and share public documentation." })}
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-[var(--ds-space-control-gap)]">
                <Label htmlFor="ca-explanation" className="text-sm">
                  {t("breachPanel.caCard.caExplanationLabel", { defaultValue: "Action Explanation" })}
                </Label>
                <Textarea
                  id="ca-explanation"
                  rows={4}
                  placeholder={t("breachPanel.caCard.caExplanationPlaceholder", { defaultValue: "Describe the action and scope" })}
                  className="resize-none"
                />
              </div>
              <div className="flex justify-end">
                <Button type="button" className="cursor-pointer">
                  {t("breachPanel.caCard.saveButton", { defaultValue: "Save Action" })}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border bg-card shadow-[var(--shadow-surface)]">
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                {t("breachPanel.docsCard.title", { defaultValue: "Public Documents" })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground break-all">
                {t("breachPanel.docsCard.description", { defaultValue: "Search and download public-facing policies, procedures and notification templates." })}
              </p>

              <div className="space-y-2">
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("breachPanel.docsCard.searchPlaceholder", { defaultValue: "Search documents by name or description" })}
                  className="h-9 text-sm"
                />
              </div>

              <div className="mt-2 h-64 pr-1 table-scroll-container space-y-2">
                {filteredDocuments.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    {t("breachPanel.docsCard.emptyState", { defaultValue: "No documents match your search." })}
                  </p>
                ) : (
                  filteredDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between gap-[var(--ds-space-3)] rounded-[var(--ds-radius-control)] border border-border bg-muted/50 px-[var(--ds-space-3)] py-[var(--ds-space-2)] text-sm"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium line-clamp-1">{doc.title}</span>
                        {doc.description && (
                          <span className="text-xs text-muted-foreground line-clamp-2">
                            {doc.description}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="cursor-pointer whitespace-nowrap"
                      >
                        {t("breachPanel.docsCard.downloadButton", { defaultValue: "Download" })}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-between gap-[var(--ds-space-3)]">
          {onMobileSwitch && (
            <Button
              variant="link"
              className="lg:hidden text-muted-foreground p-0 h-auto"
              onClick={onMobileSwitch}
            >
              {t("page.backToLogin", { defaultValue: "← Back to Login" })}
            </Button>
          )}
          <button
            type="button"
            onClick={() => setActiveView(activeView === "ca" ? "docs" : "ca")}
            className="ml-auto cursor-pointer break-all text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline motion-reduce:transition-none md:text-sm"
          >
            {activeView === "ca"
              ? t("breachPanel.toggle.toDocs", { defaultValue: "View public documents" })
              : t("breachPanel.toggle.toCa", { defaultValue: "Back to breach notification" })}
          </button>
        </div>
      </div>
    </div>
  );
}
